import { JSX, useContext, useEffect, useState } from "react";
import {
  ClaimsInsightsRankingRequestTypeEnum,
  ClaimsInsightsScoreTypeEnum,
} from "@app/projects/network-assessment/pci-dashboard/claims-insights";
import { getPciDashboardScenarioChangeDisplay, getScenarioDisplay, getRoundingDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { ColumnConfig, printNumberAsString, TextAlignment, wafflesKeyColumnName, WafflesThemes } from "@app/shared/waffles/exports";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ClaimsInsightsProviderScenarioResult } from "@app/projects/network-assessment/pci-dashboard/claims-insights/ClaimsInsightsProviderScenarioResult";
import { PciDashboardScenarioTypeEnum } from "@app/projects/network-assessment/pci-dashboard/shared";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";
import { formatDateTime } from "@app/shared/formatDateTime";
import { QESButton } from "@app/shared/qes-standards/Button";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import { Waffles } from "@app/shared/waffles/Waffles";

interface ClaimsInsightsProviderScenarioTileProps {
  data: ClaimsInsightsProviderScenarioResult[];
  loading: boolean;
  onProviderScenarioDashboardDetailsViewClick: (
    insightsCategory: ClaimsInsightsScoreTypeEnum,
    providerNetworkType: ProviderNetworkTypeEnum,
    rankingRequestType: ClaimsInsightsRankingRequestTypeEnum,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => void;
  rankingRequestType: ClaimsInsightsRankingRequestTypeEnum;
}

interface ClaimsInsightsProviderScenarioWaffleToppings {
  category: JSX.Element;
  categoryCount: string | JSX.Element;
  changeCount: JSX.Element;
  compareCategoryCount: string | JSX.Element;
  [wafflesKeyColumnName]: string;
}

const ClaimsInsightsProviderScenarioTile = (props: ClaimsInsightsProviderScenarioTileProps): JSX.Element => {
  const [tableData, setTableData] = useState<ClaimsInsightsProviderScenarioWaffleToppings[]>([]);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);

  useEffect(() => {
    if (props.data) {
      /* oxlint-disable sort-keys */
      const newTableData: ClaimsInsightsProviderScenarioWaffleToppings[] = props.data.map((s, index) => {
        return {
          [wafflesKeyColumnName]: s.category || index.toString(),
          category: (
            <span className="demibold" >
              {s.category}
            </span>
          ),
          categoryCount: getEffectivenessAndEfficiencyDisplay(s, "categoryCount"),
          compareCategoryCount: getEffectivenessAndEfficiencyDisplay(s, "compareCategoryCount"),
          changeCount: getPciDashboardScenarioChangeDisplay(s.changeCount, s.category === ClaimsInsightsScoreTypeEnum.AverageScore),
        }
      });
      /* oxlint-enable sort-keys */
      setTableData(newTableData);
    }
  }, [props.data, pciDashboardFiltersContext.displayedFilters]);

  const columnConfig: ColumnConfig = {
    category: {
      alignment: TextAlignment.Left,
      displayText: "",
      noSort: true,
      testId: "category",
    },
    categoryCount: {
      alignment: TextAlignment.Center,
      displayText: "Latest Timeslice Count",
      noSort: true,
      testId: "category-count",
    },
    changeCount: {
      alignment: TextAlignment.Center,
      displayText: "Change",
      noSort: true,
      testId: "change-count",
    },
    compareCategoryCount: {
      alignment: TextAlignment.Center,
      displayText: `${getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)} Count`,
      noSort: true,
      testId: "compare-category-count",
    },
  };

  const rowDecorator = (row: ClaimsInsightsProviderScenarioWaffleToppings) => {
    if (row[wafflesKeyColumnName] === ClaimsInsightsScoreTypeEnum.Total) {
      return "footer";
    }
  }

  const getEffectivenessAndEfficiencyDisplay = (score: ClaimsInsightsProviderScenarioResult, key: keyof ClaimsInsightsProviderScenarioResult) => {
    if (!score[key]) {
      return "---";
    }

    if (score.category === ClaimsInsightsScoreTypeEnum.AverageScore) {
      return getRoundingDisplay((score[key] as number), false, 2);
    } else {
      return (
        <QESButton
          onClick={() => props.onProviderScenarioDashboardDetailsViewClick(
            score.category,
            ProviderNetworkTypeEnum.Network,
            props.rankingRequestType,
            key === "compareCategoryCount" ? pciDashboardFiltersContext.displayedFilters.compareScenario : PciDashboardScenarioTypeEnum.Original
          )}
          qesTestId={`claims-insights__provider-scenario--dashboard-details-${props.rankingRequestType}-${score.category}`}
          qesType="secondary"
        >
          {printNumberAsString(score[key] as number, "---")}
        </QESButton>
      );
    }
  }

  return (
    <DashboardTile
      classNames="pci-dashboard__summary-tile"
      loading={props.loading}
      subtitle={`Comparison of ${formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated)} and ${getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)}`}
      title={`Network - ${props.rankingRequestType}`}
      widthClass="col-md-6"
    >
      <Waffles<ClaimsInsightsProviderScenarioWaffleToppings>
        columns={columnConfig}
        data={tableData}
        disableStickyHeader={true}
        fullWidth={true}
        keyColumns={["scoreType"]}
        rowDecorator={rowDecorator}
        testId={`claims-insights__provider-scenario--network-${props.rankingRequestType}`}
        theme={WafflesThemes.Simple}
      />
    </DashboardTile>
  )
}

export default ClaimsInsightsProviderScenarioTile;